// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — action-level permission hook (Phase 0.2)
//
//   const canApprove = useHasPermission('quotation.can_approve');
//   {canApprove && <Button onClick={approve}>Approve</Button>}
//
// PRESENTATION ONLY. This hides controls a user cannot use so the UI does not
// offer actions that will 403. It is NOT a security boundary — the server
// resolves and enforces the same keys on every request, and a client that lies
// about its permissions still gets refused. Never use this hook as the only
// thing standing between a user and a privileged action.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { useUser } from '@/shared/hooks/useAuth';

/** The caller's action-level keys as a Set, memoised per user identity. */
export function useActionPermissions(): ReadonlySet<string> {
  const user = useUser();
  const keys = user?.actionPermissions;
  return useMemo(() => new Set(keys ?? []), [keys]);
}

/**
 * Does the current user hold this catalogue key?
 *
 * Returns false while the session is still loading, so controls stay hidden
 * until the answer is known rather than flashing into view and disappearing.
 */
export function useHasPermission(key: string): boolean {
  const permissions = useActionPermissions();
  return permissions.has(key);
}

/** True when the user holds EVERY key — for controls needing a combination. */
export function useHasAllPermissions(...keys: string[]): boolean {
  const permissions = useActionPermissions();
  return keys.every((k) => permissions.has(k));
}

/** True when the user holds ANY of the keys — for a menu with mixed entries. */
export function useHasAnyPermission(...keys: string[]): boolean {
  const permissions = useActionPermissions();
  return keys.some((k) => permissions.has(k));
}
